import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {Serializer, SerializerResponse, Serializers} from '../interfaces/serializer';
import {DataSource} from '../interfaces/data-source';
import {map, tap} from 'rxjs/operators';
import {ReportUtils} from '../libraries/report-utils';
import {SerializerUtils} from '../libraries/serializer-utils';
import {AppService} from './app.service';

import {DataSourceDimension} from '../interfaces/dimensions';

@Injectable()
export class DataSetUsagesService {

  private readonly baseUrl = environment.baseUrl;

  constructor(
    private readonly _http: HttpClient,
    private readonly _appS: AppService,
  ) {}

  public downloadCustomImport(datasetID: number, customImportsID: number): Observable<any> {
    return this._http.get<Observable<any>>(`${this.baseUrl}/api/${datasetID}/custom-imports/${customImportsID}/download-file`, {
      observe: 'response',
      responseType: 'blob' as 'json'
    }).pipe(
      tap((resp) => {
        ReportUtils.downloadFile(resp);
      })
    );
  }

  public deleteCustomImport(customImportsID: number): Observable<any> {
    return this._http.delete<Observable<any>>(`${this.baseUrl}/api/${this._appS.datasetID}/custom-imports/${customImportsID}`);
  }

  public getCustomImports(): Observable<SerializerResponse<Serializers<any>>> {
    return this._http.get<any>(`${this.baseUrl}/api/${this._appS.datasetID}/custom-imports`)
      .pipe(
        map((data: SerializerResponse<Serializers<any>>) => {
          return SerializerUtils.joinRelationships(data, ['data_set', 'data_set_usage', 'custom_import_configuration']);
        })
      );
  }

  public show(dataSetUsageId: number): Observable<SerializerResponse<Serializer<DataSource>>> {
    return this._http.get<SerializerResponse<Serializer<DataSource>>>(this.baseUrl + '/api/' + this._appS.datasetID + '/data-set-usages/' + dataSetUsageId)
      .pipe(
        tap((usage) => {
          SerializerUtils.joinRelationships(usage, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
        }));
  }

  public create(dataSourceSlug: string, parameters = {}): Observable<SerializerResponse<Serializer<DataSource>>> {
    return this._http.post<SerializerResponse<Serializer<DataSource>>>(`${this.baseUrl}/api/${this._appS.datasetID}/data-set-usages`, {
      data_source: dataSourceSlug,
      parameters:  parameters
    }).pipe(
      tap((dataSetUsage) => {
        SerializerUtils.joinRelationships(dataSetUsage, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
      })
    );
  }

  public finalize(dataSetUsageId: number, dataSourceId: number): Observable<Serializer<DataSource>> {
    return this._http.post<SerializerResponse<Serializer<DataSource>>>(`${this.baseUrl}/api/${this._appS.datasetID}/data-set-usages/${dataSetUsageId}/finalize`, {
      data_source_id: dataSourceId
    }).pipe(map((dataSetUsage) => {
      return dataSetUsage.data;
    }))
  }

  public getDataSetUsages(chart: boolean = true, type: string = null): Observable<SerializerResponse<Serializers<DataSource>>> {
    let params = '?index_type=api&chart=' + chart;
    if (type !== null) {
      params += '&type=' + type;
    }

    return this._http.get<SerializerResponse<Serializers<DataSource>>>(this.baseUrl + '/api/' + this._appS.datasetID + '/data-set-usages/' + params)
      .pipe(
        tap((usages) => {
          SerializerUtils.joinRelationships(usages, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
          this._appS.dataSetUsages = usages.data;
        }));
  }

  public reloadAndGetDataSetUsageSiteCustomDimensions(dataSetUsageId: number): Observable<Serializers<DataSourceDimension>> {
    return this._http.get<SerializerResponse<Serializers<DataSourceDimension>>>(this.baseUrl + '/api/' + this._appS.datasetID + '/data-set-usages/' + dataSetUsageId + '/reload-site-custom-dimensions')
      .pipe(
        map((dataSetDimensions) => {
          return dataSetDimensions.data;
        })
      );
  }

  public getAttributionInfos(dataSetUsageId: number): Observable<any> {
    return this._http.get(this.baseUrl + '/api/' + this._appS.datasetID + '/data-set-usages/' + dataSetUsageId + '/site-attribution-infos');
  }

  public getCustomDataSetUsages(chart: boolean = true): Observable<SerializerResponse<Serializers<DataSource>>> {
    return this._http.get<SerializerResponse<Serializers<DataSource>>>(this.baseUrl + '/api/' + this._appS.datasetID + '/data-set-usages/?index_type=custom&chart=' + chart)
      .pipe(
        tap((usages) => {
          SerializerUtils.joinRelationships(usages, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
        })
      );
  }

  public updateParameters(dataSetUsageId: number, parameterKey: string, parameterValue: any, silent: boolean = false): Observable<SerializerResponse<Serializer<DataSource>>>  {
    return this._http.put<SerializerResponse<Serializer<DataSource>>>(`${this.baseUrl}/api/${this._appS.datasetID}/data-set-usages/${dataSetUsageId}/parameters`, {
      parameter_key:   parameterKey,
      parameter_value: parameterValue,
      silent: silent
    }).pipe(
      tap((dataSetUsage) => {
        SerializerUtils.joinRelationships(dataSetUsage, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
      })
    )
  }

  public getDataSourceImportedData(dataSourceID: number): Observable<SerializerResponse<Serializer<DataSource>>> {
    return this._http.get(`${this.baseUrl}/api/${this._appS.datasetID}/data-set-usages/${dataSourceID}?chart=true`)
      .pipe(
        tap((data: any) => {
          SerializerUtils.joinRelationships(data, ['data_source', 'data_set']);
        })
      );
  }

  public updateDataSource(data_set_usage_id, data): Observable<SerializerResponse<Serializer<DataSource>>> {
    return this._http.put<any>(this.baseUrl + '/api/'+this._appS.datasetID+'/data-set-usages/' + data_set_usage_id, data)
      .pipe(
        tap((usage) => {
          SerializerUtils.joinRelationships(usage, ['data_source', 'data_set', 'user_data_source_credential', 'data_set_usage_custom_configuration']);
        })
      );
  }

  public updateCustomImportConfiguration(customImportConfiguration, data): Observable<any> {
    return this._http.put<any>(this.baseUrl + '/api/'+this._appS.datasetID+'/custom-import-configurations/' + customImportConfiguration.id, data);
  }

  public checkAdloopScript(data_set_id, data_set_usage_id): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/api/${data_set_id}/data-set-usages/${data_set_usage_id}/check-script`)
  }
}
